/**
 * <s-newsletter-popup> — delayed newsletter signup dialog.
 *
 * Wraps an <s-dialog> + native <dialog> containing Shopify's native customer
 * form. Opens after `data-delay` seconds unless the visitor dismissed or
 * submitted within the last `data-suppress-days` days (localStorage). After a
 * native form post, the server renders [data-newsletter-result] (success or
 * errors) and the dialog reopens immediately so the visitor sees the outcome.
 *
 * Attributes:
 *   data-delay           — seconds before the first open (default 5)
 *   data-suppress-days   — days to stay hidden after dismiss/submit (default 7)
 *   data-storage-key     — localStorage key
 *   data-ignore-suppression — present in the theme editor so previews always show it
 */
const DAY_MS = 24 * 60 * 60 * 1000;

class SNewsletterPopup extends HTMLElement {
  #ac;
  #timer = null;

  connectedCallback() {
    const dialog = this.querySelector('dialog');
    if (!dialog) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    dialog.addEventListener('close', () => this.#suppress(), opts);
    this.querySelector('form')?.addEventListener('submit', () => this.#suppress(), opts);

    // After a native post, always show the result (thanks message or errors).
    if (this.querySelector('[data-newsletter-result]')) {
      if (this.querySelector('[data-newsletter-success]')) this.#suppress();
      this.#open(dialog);
      return;
    }

    if (this.#isSuppressed()) return;

    const delay = Math.max(0, Number(this.dataset.delay ?? 5)) * 1000;
    this.#timer = setTimeout(() => this.#openWhenClear(dialog), delay);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearTimeout(this.#timer);
  }

  // Never stack on top of another open modal (cart drawer, search, menu); retry shortly.
  #openWhenClear(dialog) {
    if (document.querySelector('dialog:modal')) {
      this.#timer = setTimeout(() => this.#openWhenClear(dialog), 4000);
      return;
    }
    this.#open(dialog);
  }

  #open(dialog) {
    if (dialog.open) return;
    dialog.showModal();
  }

  #storageKey() {
    return this.dataset.storageKey || 'newsletter-popup-suppressed-at';
  }

  #isSuppressed() {
    if (this.hasAttribute('data-ignore-suppression')) return false;
    try {
      const at = Number(localStorage.getItem(this.#storageKey()));
      const days = Number(this.dataset.suppressDays ?? 7);
      return Number.isFinite(at) && at > 0 && Date.now() - at < days * DAY_MS;
    } catch {
      return false;
    }
  }

  #suppress() {
    try {
      localStorage.setItem(this.#storageKey(), String(Date.now()));
    } catch {
      // Storage unavailable (private mode); the popup may reappear next visit.
    }
  }
}

if (!customElements.get('s-newsletter-popup')) {
  customElements.define('s-newsletter-popup', SNewsletterPopup);
}
